/**
 * NOYAU D'ÉDITION GÉOMÉTRIQUE — sommets, accrochage, contraintes, historique.
 *
 * ⚠️⚠️ POURQUOI CE FICHIER EXISTE. Mesuré le 23/09/2026 : le serveur accepte
 * déjà une géométrie éditée — `POST /parcelles/verifier-emprise` dit à un
 * géomètre si son emprise recouvre une voisine AVANT qu'il ne la dépose, et
 * `modules/releves.ts` accepte `Polygon`/`MultiPolygon`. **Aucun écran ne sait
 * en produire une.** Sonde jouée sur `frontend/` : accrochage 0 fichier,
 * contraintes géométriques 0, cotation 0 ; une seule dépendance carto
 * (`maplibre-gl`), aucune bibliothèque de dessin ; les quatre occurrences
 * d'« annuler » disent « Annuler cette affaire », jamais un `undo`.
 *
 * Le moteur est profond et n'a pas de main pour le tenir. Ce fichier est la
 * main. Il est PUR : aucune dépendance au DOM, à la carte, ni à la base — donc
 * il s'éprouve sans écran et sans tunnel, et le serveur peut rejouer le même
 * code pour vérifier ce que le client prétend avoir dessiné.
 *
 * ⚠️ QUATRE DÉCISIONS PORTENT TOUT LE RESTE. Elles sont écrites ici parce
 * qu'elles ne se devinent pas à la lecture des fonctions :
 *
 *  1. LES TOLÉRANCES SONT EN MÈTRES, JAMAIS EN DEGRÉS. À la latitude de
 *     Conakry (9,5° N), un degré de longitude vaut ~109,8 km et un degré de
 *     latitude ~110,6 km : une tolérance exprimée en degrés est ANISOTROPE.
 *     Accrocher « à 0,00001° » accroche plus loin en longitude qu'en latitude,
 *     et l'écart grandit vers le nord du pays. Un géomètre qui demande 10 cm
 *     doit obtenir 10 cm dans les deux axes.
 *
 *  2. LES SOMMETS ONT UNE IDENTITÉ STABLE. Un sommet n'est pas une position :
 *     c'est un objet qui GARDE son `id` quand il bouge. Sans cela, annuler un
 *     déplacement puis une insertion faite avant lui réapplique le mouvement
 *     au mauvais point — l'index a glissé. C'est le défaut classique des
 *     éditeurs qui indexent par position.
 *
 *  3. L'HISTORIQUE STOCKE DES COMMANDES INVERSIBLES, PAS DES INSTANTANÉS.
 *     Un anneau de 500 sommets × 100 annulations en instantanés, c'est 50 000
 *     sommets en mémoire pour rien. Chaque commande porte de quoi se défaire
 *     seule, en O(1).
 *
 *  4. AUCUNE OPÉRATION NE REND UNE FIGURE INVALIDE EN SILENCE. Toute sortie
 *     passe par `diagnostiquer()`. Un éditeur qui laisse produire un anneau
 *     auto-sécant déplace le refus vers le dépôt, c'est-à-dire dix minutes
 *     plus tard, là où l'agent a déjà oublié le geste qui l'a causé.
 *
 * ⚠️ CE QUE CE FICHIER NE FAIT PAS, ET NE DOIT PAS FAIRE. Il ne dit pas si une
 * emprise recouvre une voisine : c'est `cad.recouvrements()` qui le sait, avec
 * l'index GIST et les tolérances du territoire. Recalculer le recouvrement ici
 * produirait une seconde vérité qui divergerait de la base au premier
 * correctif appliqué à une seule — le défaut le plus cher de ce dépôt. L'écran
 * appelle le serveur pendant le tracé ; ce noyau ne fait que la géométrie
 * locale, celle qui doit répondre au millième de seconde sous le curseur.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Le modèle
// ─────────────────────────────────────────────────────────────────────────────

/** Un point en WGS84, comme la base les stocke (`ST_SetSRID(…, 4326)`). */
export interface Point {
  lon: number;
  lat: number;
}

/** Un sommet : une position QUI A UNE IDENTITÉ (décision 2). */
export interface Sommet extends Point {
  readonly id: string;
}

/**
 * Un anneau fermé. La fermeture est IMPLICITE : le dernier sommet n'est jamais
 * une copie du premier.
 *
 * ⚠️ C'est le contraire de GeoJSON, qui exige la répétition. La conversion se
 * fait aux frontières (`versGeoJSON` / `depuisGeoJSON`) et nulle part ailleurs.
 * Porter la répétition dans le modèle d'édition oblige chaque opération à se
 * demander « et si c'est le sommet de fermeture ? » — et celle qui oublie de
 * se le demander produit un anneau ouvert sans qu'aucun type ne bronche.
 */
export interface Anneau {
  readonly id: string;
  sommets: Sommet[];
}

/**
 * CE QUE LA FIGURE REPRÉSENTE — et la seule chose qui change entre les trois.
 *
 * ⚠️⚠️ UNE SEULE MACHINERIE, PAS TROIS. Bornes, voies et parcelles auraient pu
 * avoir chacune leur éditeur : c'est le réflexe, et ce serait la faute que ce
 * dépôt a déjà payée deux fois (le SDK de Connect forké en deux exemplaires
 * divergents, `carte.tsx` contre `assemblage.tsx`). Trois implémentations de
 * l'accrochage divergent au premier correctif appliqué à une seule — et l'on
 * ne découvre laquelle est restée fausse qu'en la cherchant.
 *
 * Un point est un anneau d'UN sommet, une ligne un anneau OUVERT, une surface
 * un anneau FERMÉ. L'accrochage, l'historique, la métrique et le diagnostic
 * sont alors littéralement le même code.
 *
 * ⚠️ LA FERMETURE EST LA SEULE VRAIE BIFURCATION. `cotes()` refermait
 * systématiquement sur le premier sommet (`(i + 1) % n`). Laissé tel quel pour
 * une ligne, il aurait inventé un segment de retour entre le dernier point et
 * le premier — une voie mesurée trop longue, et une rue qui se referme en
 * boucle sur la carte sans qu'aucune erreur ne soit levée.
 */
export type Nature = 'point' | 'ligne' | 'surface';

/**
 * Une figure : un ou plusieurs anneaux.
 *
 * ⚠️ `nature` EST FACULTATIVE ET VAUT « surface » PAR DÉFAUT. Le noyau a vécu
 * plusieurs jours sans elle ; la rendre obligatoire aurait cassé d'un coup
 * l'outil de tracé, ses 44 essais et le contrat serveur — pour une valeur que
 * tous ces appels sous-entendaient déjà.
 */
export interface Figure {
  anneaux: Anneau[];
  nature?: Nature;
}

/** La nature d'une figure, avec son défaut historique. */
export function natureDe(f: Figure): Nature {
  return f.nature ?? 'surface';
}

/** Une figure se referme-t-elle sur son premier sommet ? */
export function estFermee(f: Figure): boolean {
  return natureDe(f) === 'surface';
}

export type Gravite = 'bloquant' | 'avertissement';

export interface Diagnostic {
  readonly regle: string;
  readonly gravite: Gravite;
  readonly message: string;
  readonly anneauId?: string;
  readonly sommetId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Métrique locale — la décision 1, isolée en un seul endroit
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Le plan local tangent au point de référence. Toutes les distances du noyau
 * passent par ici, et seulement par ici.
 *
 * ⚠️ POURQUOI PAS UNE VRAIE PROJECTION (UTM, géodésique inverse). Parce qu'on
 * mesure des distances de l'ordre du mètre sous un curseur, à chaque
 * déplacement de souris. L'erreur d'un plan tangent équirectangulaire croît
 * comme le carré du rapport distance/rayon terrestre : sur 1 km elle est de
 * l'ordre de 10⁻⁸ en relatif, soit très en deçà du micron. Elle serait fausse
 * pour mesurer une frontière régionale ; elle est exacte pour accrocher.
 *
 * ⚠️ ET LA RÉFÉRENCE SUIT LE CHANTIER. Figer le facteur de longitude sur un
 * point du sud du pays fausserait l'accrochage dans le nord — la Guinée
 * s'étend de 7,2° à 12,7° N, où le mètre par degré de longitude passe de
 * 110 447 m à 108 614 m, soit **1,66 %** (calculé, pas estimé : une première
 * rédaction de ce commentaire annonçait 3,5 % et l'essai l'a démentie). Sur
 * une parcelle de 100 m, 1,66 % fait 1,66 m — très au-delà de toute tolérance
 * d'accrochage. On reprojette donc autour du point courant.
 */
export interface PlanLocal {
  readonly metresParDegreLat: number;
  readonly metresParDegreLon: number;
}

/**
 * Formule de la Terre ellipsoïdale (WGS84), développement en série usuel.
 * Exacte à mieux que le décimètre par degré sur toute la plage de latitudes.
 */
export function planLocal(latitudeDeg: number): PlanLocal {
  const f = (latitudeDeg * Math.PI) / 180;
  return {
    metresParDegreLat: 111_132.92 - 559.82 * Math.cos(2 * f) + 1.175 * Math.cos(4 * f) - 0.0023 * Math.cos(6 * f),
    metresParDegreLon: 111_412.84 * Math.cos(f) - 93.5 * Math.cos(3 * f) + 0.118 * Math.cos(5 * f),
  };
}

/** Coordonnées métriques locales, relatives au point de référence. */
function versMetres(p: Point, ref: Point, plan: PlanLocal): { x: number; y: number } {
  return {
    x: (p.lon - ref.lon) * plan.metresParDegreLon,
    y: (p.lat - ref.lat) * plan.metresParDegreLat,
  };
}

function versDegres(m: { x: number; y: number }, ref: Point, plan: PlanLocal): Point {
  return {
    lon: ref.lon + m.x / plan.metresParDegreLon,
    lat: ref.lat + m.y / plan.metresParDegreLat,
  };
}

/** Distance en mètres entre deux points. */
export function distanceM(a: Point, b: Point): number {
  const plan = planLocal((a.lat + b.lat) / 2);
  const dx = (a.lon - b.lon) * plan.metresParDegreLon;
  const dy = (a.lat - b.lat) * plan.metresParDegreLat;
  return Math.hypot(dx, dy);
}

// ─────────────────────────────────────────────────────────────────────────────
// Construction
// ─────────────────────────────────────────────────────────────────────────────

let compteur = 0;
/**
 * ⚠️ IDENTIFIANT LOCAL, PAS UN IDENTIFIANT DE REGISTRE. Il ne sert qu'à suivre
 * un sommet pendant la session d'édition. Le confondre avec les identifiants
 * stables de `cad.parcelle` (migration 007) ferait croire qu'un sommet survit
 * à un rechargement : il ne survit pas, et ne doit pas.
 */
export function identifiantLocal(prefixe: string): string {
  compteur += 1;
  return `${prefixe}-${compteur.toString(36)}`;
}

export function sommet(lon: number, lat: number, id = identifiantLocal('s')): Sommet {
  return { id, lon, lat };
}

export function anneau(points: readonly Point[], id = identifiantLocal('a')): Anneau {
  return { id, sommets: points.map((p) => sommet(p.lon, p.lat)) };
}

export function figure(exterieur: readonly Point[], ...trous: readonly Point[][]): Figure {
  return { nature: 'surface', anneaux: [anneau(exterieur), ...trous.map((t) => anneau(t))] };
}

/** Un semis de points : bornes, adresses, équipements. */
export function semis(points: readonly Point[] = []): Figure {
  return { nature: 'point', anneaux: [anneau(points)] };
}

/** Une polyligne ouverte : voie, réseau, tronçon. */
export function polyligne(points: readonly Point[] = []): Figure {
  return { nature: 'ligne', anneaux: [anneau(points)] };
}

/** Copie profonde — l'historique ne doit jamais partager de sommet avec la figure vivante. */
export function copier(f: Figure): Figure {
  /* ⚠️ LA NATURE SE COPIE AUSSI. L'oublier faisait rouvrir toute session sur
     une « surface » : une voie de trois points y devenait un anneau refusé
     pour « moins de trois sommets », et le refus nommait une règle qui ne la
     concernait pas. */
  return {
    nature: f.nature,
    anneaux: f.anneaux.map((a) => ({ id: a.id, sommets: a.sommets.map((s) => ({ ...s })) })),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Mesures d'anneau
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Aire signée en m². Positive si l'anneau tourne dans le sens antihoraire.
 *
 * ⚠️ LE SIGNE EST UNE INFORMATION, PAS UN ARTEFACT. GeoJSON (RFC 7946) veut le
 * contour extérieur en antihoraire et les trous en horaire. Un trou orienté à
 * l'envers est accepté par beaucoup de lecteurs et rejeté par PostGIS —
 * l'incohérence ne se découvre alors qu'au dépôt.
 */
/**
 * Les côtés d'un anneau fermé : chaque couple de sommets consécutifs, le
 * dernier refermant sur le premier.
 *
 * ⚠️ CET ITÉRATEUR N'EST PAS UN CONFORT D'ÉCRITURE, C'EST UNE GARDE. Le projet
 * compile avec `noUncheckedIndexedAccess` : `sommets[i]` a le type
 * `Sommet | undefined`, et le compilateur a relevé 54 accès non gardés dans
 * une première rédaction de ce fichier. Les taire avec `!` aurait rendu le
 * typage vert en laissant passer, à l'exécution, un `undefined` qui produit
 * `NaN` — et un NaN en géométrie ne lève rien : il se propage jusqu'à une aire
 * « vide » ou un accrochage qui n'accroche jamais, sans un message. Passer par
 * un itérateur supprime l'indexation au lieu de la masquer.
 */
function* cotes(
  sommets: readonly Sommet[],
  ferme = true,
): Generator<readonly [Sommet, Sommet, number]> {
  const n = sommets.length;
  // ⚠️ UNE LIGNE A UN CÔTÉ DE MOINS QU'UNE SURFACE. Le segment de retour
  // n'existe pas : l'ajouter allongerait toute voie de la distance entre ses
  // deux extrémités, et la dessinerait refermée en boucle.
  const dernier = ferme ? n : n - 1;
  for (let i = 0; i < dernier; i += 1) {
    const a = sommets[i];
    const b = sommets[(i + 1) % n];
    if (a && b) yield [a, b, i] as const;
  }
}

export function aireSigneeM2(a: Anneau): number {
  if (a.sommets.length < 3) return 0;
  const ref = a.sommets[0];
  if (!ref) return 0;
  const plan = planLocal(ref.lat);
  let somme = 0;
  for (const [p0, q0] of cotes(a.sommets)) {
    const p = versMetres(p0, ref, plan);
    const q = versMetres(q0, ref, plan);
    somme += p.x * q.y - q.x * p.y;
  }
  return somme / 2;
}

export function aireM2(a: Anneau): number {
  return Math.abs(aireSigneeM2(a));
}

/**
 * Périmètre d'un anneau FERMÉ. Pour une ligne, employer `longueurM`.
 *
 * ⚠️ LES DEUX NOMS EXISTENT PARCE QUE LES DEUX MESURES SONT DIFFÉRENTES, et
 * qu'un seul nom aurait fait passer l'une pour l'autre. Le périmètre d'une
 * parcelle compte le retour au point de départ ; la longueur d'une voie non.
 */
export function perimetreM(a: Anneau): number {
  if (a.sommets.length < 2) return 0;
  let total = 0;
  for (const [p, q] of cotes(a.sommets, true)) total += distanceM(p, q);
  return total;
}

/** Longueur développée d'une ligne — sans segment de retour. */
export function longueurM(a: Anneau): number {
  if (a.sommets.length < 2) return 0;
  let total = 0;
  for (const [p, q] of cotes(a.sommets, false)) total += distanceM(p, q);
  return total;
}

/** La mesure qui convient à la nature : longueur pour une ligne, périmètre sinon. */
export function mesureLineaireM(f: Figure): number {
  const a = f.anneaux[0];
  if (!a) return 0;
  return natureDe(f) === 'ligne' ? longueurM(a) : perimetreM(a);
}

/** Aire nette de la figure : extérieur moins les trous. */
export function aireNetteM2(f: Figure): number {
  /* ⚠️ ZÉRO POUR UN POINT ET UNE LIGNE, ET C'EST UNE VÉRITÉ, PAS UN DÉFAUT.
     `aireSigneeM2` appliquée à une polyligne rendrait l'aire du polygone
     qu'on obtiendrait en la refermant — un nombre plausible, affiché en m²,
     et faux. Une borne n'a pas de surface ; une voie non plus. */
  if (natureDe(f) !== 'surface') return 0;
  const [ext, ...trous] = f.anneaux;
  if (!ext) return 0;
  return Math.max(0, aireM2(ext) - trous.reduce((s, t) => s + aireM2(t), 0));
}

// ─────────────────────────────────────────────────────────────────────────────
// Segments et projection — le socle de l'accrochage
// ─────────────────────────────────────────────────────────────────────────────

export interface Segment {
  readonly anneauId: string;
  readonly index: number;
  readonly a: Sommet;
  readonly b: Sommet;
}

export function segments(f: Figure): Segment[] {
  const out: Segment[] = [];
  const ferme = estFermee(f);
  for (const an of f.anneaux) {
    for (const [a, b, index] of cotes(an.sommets, ferme)) {
      out.push({ anneauId: an.id, index, a, b });
    }
  }
  return out;
}

/** Projeté orthogonal d'un point sur un segment, borné aux extrémités. */
export function projeterSurSegment(p: Point, a: Point, b: Point): { point: Point; distanceM: number; t: number } {
  const ref = a;
  const plan = planLocal((a.lat + b.lat) / 2);
  const P = versMetres(p, ref, plan);
  const A = { x: 0, y: 0 };
  const B = versMetres(b, ref, plan);
  const vx = B.x - A.x;
  const vy = B.y - A.y;
  const len2 = vx * vx + vy * vy;
  // Segment dégénéré : le projeté est l'extrémité, et `t` vaut 0 — pas NaN.
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, (P.x * vx + P.y * vy) / len2));
  const proj = { x: A.x + t * vx, y: A.y + t * vy };
  return {
    point: versDegres(proj, ref, plan),
    distanceM: Math.hypot(P.x - proj.x, P.y - proj.y),
    t,
  };
}

/** Deux segments se croisent-ils, et où ? `null` si parallèles ou disjoints. */
export function intersection(a1: Point, a2: Point, b1: Point, b2: Point): Point | null {
  const ref = a1;
  const plan = planLocal((a1.lat + a2.lat + b1.lat + b2.lat) / 4);
  const A = versMetres(a1, ref, plan);
  const B = versMetres(a2, ref, plan);
  const C = versMetres(b1, ref, plan);
  const D = versMetres(b2, ref, plan);
  const d = (B.x - A.x) * (D.y - C.y) - (B.y - A.y) * (D.x - C.x);
  if (Math.abs(d) < 1e-12) return null;
  const t = ((C.x - A.x) * (D.y - C.y) - (C.y - A.y) * (D.x - C.x)) / d;
  const u = ((C.x - A.x) * (B.y - A.y) - (C.y - A.y) * (B.x - A.x)) / d;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return versDegres({ x: A.x + t * (B.x - A.x), y: A.y + t * (B.y - A.y) }, ref, plan);
}

// ─────────────────────────────────────────────────────────────────────────────
// Accrochage
// ─────────────────────────────────────────────────────────────────────────────

export type NatureAccroche = 'sommet' | 'segment' | 'intersection';

export interface Accroche {
  readonly point: Point;
  readonly nature: NatureAccroche;
  readonly distanceM: number;
  readonly anneauId?: string;
  readonly sommetId?: string;
}

export interface OptionsAccrochage {
  /** Tolérance en MÈTRES (décision 1). */
  readonly toleranceM: number;
  /** Figures voisines : les parcelles déjà au plan, sur lesquelles on veut se coller. */
  readonly voisines?: readonly Figure[];
  /** Sommets à ignorer — typiquement celui qu'on est en train de déplacer. */
  readonly ignorer?: readonly string[];
  readonly natures?: readonly NatureAccroche[];
}

/**
 * Cherche le meilleur point d'accrochage autour de `p`.
 *
 * ⚠️⚠️ L'ORDRE DE PRÉFÉRENCE EST UN RANG, PAS UNE DISTANCE — ET LA PREMIÈRE
 * RÉDACTION S'EST TROMPÉE DE MÉCANISME. Elle accordait au sommet une « prime »
 * de 25 cm retranchée de sa distance. Deux essais l'ont démentie, et la
 * géométrie explique pourquoi la prime ne pouvait PAS marcher : quand on vise
 * près d'un coin, la perpendiculaire à un côté adjacent est une cathète là où
 * la distance au coin est l'hypoténuse — **elle lui est toujours inférieure ou
 * égale**. Mesuré sur le cas de l'essai : côté à 1,10 m, sommet à 1,56 m.
 * Aucune prime fixe ne renverse un classement que la géométrie garantit.
 *
 * Le rang est donc absolu, comme dans tout éditeur professionnel : dans la
 * tolérance, un SOMMET l'emporte sur une INTERSECTION, qui l'emporte sur un
 * CÔTÉ ; à rang égal, le plus proche gagne. Ce que cela protège : coller
 * « presque » sur une borne existante, à 2 cm à côté, crée une micro-lacune
 * que `cad.recouvrements` signalera plus tard et que personne ne saura
 * expliquer.
 *
 * ⚠️ COROLLAIRE : LA TOLÉRANCE DOIT RESTER PETITE. Le rang étant absolu, une
 * tolérance de 50 m accrocherait un sommet lointain plutôt que le côté sous le
 * curseur. La tolérance se règle à l'échelle du pixel, pas de la parcelle.
 */
const RANG_ACCROCHE: Record<NatureAccroche, number> = {
  sommet: 0,
  intersection: 1,
  segment: 2,
};

export function accrocher(p: Point, cible: Figure, options: OptionsAccrochage): Accroche | null {
  const natures = options.natures ?? (['sommet', 'segment', 'intersection'] as const);
  const ignorer = new Set(options.ignorer ?? []);
  const toutes: Figure[] = [cible, ...(options.voisines ?? [])];
  let meilleure: Accroche | null = null;

  const retenir = (c: Accroche) => {
    if (c.distanceM > options.toleranceM) return;
    if (!meilleure) { meilleure = c; return; }
    const rangC = RANG_ACCROCHE[c.nature];
    const rangM = RANG_ACCROCHE[meilleure.nature];
    if (rangC !== rangM) { if (rangC < rangM) meilleure = c; return; }
    if (c.distanceM < meilleure.distanceM) meilleure = c;
  };

  if (natures.includes('sommet')) {
    for (const f of toutes) {
      for (const an of f.anneaux) {
        for (const s of an.sommets) {
          if (ignorer.has(s.id)) continue;
          retenir({ point: { lon: s.lon, lat: s.lat }, nature: 'sommet', distanceM: distanceM(p, s), anneauId: an.id, sommetId: s.id });
        }
      }
    }
  }

  if (natures.includes('segment')) {
    for (const f of toutes) {
      for (const sg of segments(f)) {
        if (ignorer.has(sg.a.id) || ignorer.has(sg.b.id)) continue;
        const pr = projeterSurSegment(p, sg.a, sg.b);
        retenir({ point: pr.point, nature: 'segment', distanceM: pr.distanceM, anneauId: sg.anneauId });
      }
    }
  }

  if (natures.includes('intersection')) {
    const tous = toutes.flatMap((f) => segments(f));
    for (let i = 0; i < tous.length; i += 1) {
      const si = tous[i];
      if (!si) continue;
      for (let j = i + 1; j < tous.length; j += 1) {
        const sj = tous[j];
        if (!sj) continue;
        // Deux segments d'un même anneau qui se touchent par un sommet partagé
        // ne forment pas une intersection utile : c'est le sommet lui-même.
        if (si.anneauId === sj.anneauId && Math.abs(si.index - sj.index) <= 1) continue;
        const x = intersection(si.a, si.b, sj.a, sj.b);
        if (x) retenir({ point: x, nature: 'intersection', distanceM: distanceM(p, x) });
      }
    }
  }

  return meilleure;
}

// ─────────────────────────────────────────────────────────────────────────────
// Diagnostic — la décision 4
// ─────────────────────────────────────────────────────────────────────────────

/** Deux sommets plus proches que cela sont tenus pour confondus. */
export const SEUIL_DEGENERE_M = 0.01;

/**
 * ⚠️ CE DIAGNOSTIC EST LOCAL ET IL LE DIT. Il ne connaît que la figure qu'on
 * lui donne : il ne voit ni les voisines, ni les tolérances du territoire, ni
 * le ressort de la division. Un `[]` vide ne signifie donc pas « déposable »,
 * il signifie « rien que cette figure puisse contredire toute seule ». Rendre
 * les deux indiscernables serait exactement le vert obtenu faute d'avoir
 * trouvé quoi contredire.
 */
export function diagnostiquer(f: Figure): Diagnostic[] {
  const out: Diagnostic[] = [];
  const nature = natureDe(f);

  if (f.anneaux.length === 0) {
    out.push({ regle: 'figure_vide', gravite: 'bloquant', message: 'La figure ne porte aucun anneau.' });
    return out;
  }

  // ── Les natures non surfaciques ont leurs propres règles ──────────────────
  if (nature === 'point') {
    /**
     * ⚠️ UN SEMIS N'EST PAS UNE SURFACE À UN SOMMET. Le passer dans les règles
     * ci-dessous le refuserait pour « moins de trois sommets » — un message
     * exact sur une figure parfaitement bonne, et l'agent chercherait à
     * ajouter deux bornes qu'il n'a pas levées.
     */
    const an = f.anneaux[0];
    if (!an || an.sommets.length === 0) {
      out.push({ regle: 'semis_vide', gravite: 'bloquant', message: 'Aucun point posé.' });
      return out;
    }
    if (f.anneaux.length > 1) {
      out.push({
        regle: 'semis_multiple', gravite: 'avertissement',
        message: `${f.anneaux.length} groupes de points : seul le premier sera déposé.`,
      });
    }
    // Deux bornes confondues sont une saisie double, pas deux bornes.
    const vus = an.sommets;
    for (let i = 0; i < vus.length; i += 1) {
      for (let j = i + 1; j < vus.length; j += 1) {
        const a = vus[i]; const b = vus[j];
        if (a && b && distanceM(a, b) < SEUIL_DEGENERE_M) {
          out.push({
            regle: 'points_confondus', gravite: 'bloquant', anneauId: an.id, sommetId: b.id,
            message: `Deux points sont confondus (moins de ${SEUIL_DEGENERE_M * 100} cm).`,
          });
        }
      }
    }
    return out;
  }

  if (nature === 'ligne') {
    const an = f.anneaux[0];
    if (!an || an.sommets.length < 2) {
      out.push({
        regle: 'ligne_insuffisante', gravite: 'bloquant', anneauId: an?.id,
        message: `Une ligne demande au moins deux points ; elle en porte ${an?.sommets.length ?? 0}.`,
      });
      return out;
    }
    for (const [a, b] of cotes(an.sommets, false)) {
      if (distanceM(a, b) < SEUIL_DEGENERE_M) {
        out.push({
          regle: 'sommets_degeneres', gravite: 'bloquant', anneauId: an.id, sommetId: b.id,
          message: `La ligne porte deux points confondus (moins de ${SEUIL_DEGENERE_M * 100} cm).`,
        });
      }
    }
    /**
     * ⚠️ UNE LIGNE QUI SE CROISE N'EST PAS FAUTIVE — un échangeur, une boucle
     * de desserte, un réseau qui repasse sur lui-même existent. On AVERTIT, on
     * ne bloque pas : bloquer refuserait des voies réelles, et c'est le
     * genre de refus qui fait contourner l'outil plutôt que le corriger.
     */
    const sg = [...cotes(an.sommets, false)].map(([a, b]) => [a, b] as const);
    for (let i = 0; i < sg.length; i += 1) {
      for (let j = i + 2; j < sg.length; j += 1) {
        const ci = sg[i]; const cj = sg[j];
        if (ci && cj && intersection(ci[0], ci[1], cj[0], cj[1])) {
          out.push({
            regle: 'ligne_auto_secante', gravite: 'avertissement', anneauId: an.id,
            message: `La ligne se recoupe entre les segments ${i + 1} et ${j + 1}.`,
          });
        }
      }
    }
    if (longueurM(an) < 1) {
      out.push({
        regle: 'longueur_negligeable', gravite: 'avertissement', anneauId: an.id,
        message: `La ligne mesure moins d'un mètre (${longueurM(an).toFixed(2)} m).`,
      });
    }
    return out;
  }

  f.anneaux.forEach((an, rang) => {
    const role = rang === 0 ? 'Le contour' : `Le trou ${rang}`;

    if (an.sommets.length < 3) {
      out.push({
        regle: 'anneau_insuffisant', gravite: 'bloquant', anneauId: an.id,
        message: `${role} compte ${an.sommets.length} sommet(s) : il en faut au moins 3.`,
      });
      return;
    }

    // Sommets confondus — la cause la plus fréquente d'un refus PostGIS au dépôt.
    for (const [a, b] of cotes(an.sommets)) {
      if (distanceM(a, b) < SEUIL_DEGENERE_M) {
        out.push({
          regle: 'sommets_degeneres', gravite: 'bloquant', anneauId: an.id, sommetId: b.id,
          message: `${role} porte deux sommets confondus (moins de ${SEUIL_DEGENERE_M * 100} cm).`,
        });
      }
    }

    // Auto-intersection.
    const sg: Array<readonly [Sommet, Sommet]> = [...cotes(an.sommets)].map(([a, b]) => [a, b] as const);
    for (let i = 0; i < sg.length; i += 1) {
      const ci = sg[i];
      if (!ci) continue;
      for (let j = i + 1; j < sg.length; j += 1) {
        const cj = sg[j];
        if (!cj) continue;
        const adjacents = j === i + 1 || (i === 0 && j === sg.length - 1);
        if (adjacents) continue;
        if (intersection(ci[0], ci[1], cj[0], cj[1])) {
          out.push({
            regle: 'anneau_auto_secant', gravite: 'bloquant', anneauId: an.id,
            message: `${role} se recoupe lui-même entre les côtés ${i + 1} et ${j + 1}.`,
          });
        }
      }
    }

    // Orientation RFC 7946.
    const signee = aireSigneeM2(an);
    if (rang === 0 && signee < 0) {
      out.push({ regle: 'orientation', gravite: 'avertissement', anneauId: an.id, message: 'Le contour extérieur tourne dans le sens horaire ; il sera réorienté au dépôt.' });
    }
    if (rang > 0 && signee > 0) {
      out.push({ regle: 'orientation', gravite: 'avertissement', anneauId: an.id, message: `${role} tourne dans le sens antihoraire ; il sera réorienté au dépôt.` });
    }

    if (aireM2(an) < 1) {
      out.push({ regle: 'aire_negligeable', gravite: 'avertissement', anneauId: an.id, message: `${role} couvre moins d'un mètre carré (${aireM2(an).toFixed(3)} m²).` });
    }
  });

  return out;
}

export function estDeposable(f: Figure): boolean {
  return !diagnostiquer(f).some((d) => d.gravite === 'bloquant');
}

// ─────────────────────────────────────────────────────────────────────────────
// Commandes inversibles — la décision 3
// ─────────────────────────────────────────────────────────────────────────────

export type Commande =
  | { readonly type: 'deplacer'; readonly anneauId: string; readonly sommetId: string; readonly avant: Point; readonly apres: Point }
  | { readonly type: 'inserer'; readonly anneauId: string; readonly index: number; readonly sommet: Sommet }
  | { readonly type: 'supprimer'; readonly anneauId: string; readonly index: number; readonly sommet: Sommet }
  | { readonly type: 'remplacerAnneau'; readonly anneauId: string; readonly avant: readonly Sommet[]; readonly apres: readonly Sommet[] }
  | { readonly type: 'ajouterAnneau'; readonly anneau: Anneau; readonly index: number }
  | { readonly type: 'retirerAnneau'; readonly anneau: Anneau; readonly index: number };

function anneauDe(f: Figure, id: string): Anneau {
  const a = f.anneaux.find((x) => x.id === id);
  if (!a) throw new Error(`Anneau ${id} absent de la figure.`);
  return a;
}

/** Applique une commande EN PLACE. Ne valide pas : c'est le rôle de `diagnostiquer`. */
export function appliquer(f: Figure, c: Commande): void {
  switch (c.type) {
    case 'deplacer': {
      const an = anneauDe(f, c.anneauId);
      const s = an.sommets.find((x) => x.id === c.sommetId);
      if (!s) throw new Error(`Sommet ${c.sommetId} absent de l'anneau ${c.anneauId}.`);
      s.lon = c.apres.lon;
      s.lat = c.apres.lat;
      return;
    }
    case 'inserer':
      anneauDe(f, c.anneauId).sommets.splice(c.index, 0, { ...c.sommet });
      return;
    case 'supprimer':
      anneauDe(f, c.anneauId).sommets.splice(c.index, 1);
      return;
    case 'remplacerAnneau':
      anneauDe(f, c.anneauId).sommets = c.apres.map((s) => ({ ...s }));
      return;
    case 'ajouterAnneau':
      f.anneaux.splice(c.index, 0, { id: c.anneau.id, sommets: c.anneau.sommets.map((s) => ({ ...s })) });
      return;
    case 'retirerAnneau':
      f.anneaux.splice(c.index, 1);
      return;
  }
}

/** L'inverse exact d'une commande. C'est ce qui rend l'annulation O(1). */
export function inverser(c: Commande): Commande {
  switch (c.type) {
    case 'deplacer': return { ...c, avant: c.apres, apres: c.avant };
    case 'inserer': return { type: 'supprimer', anneauId: c.anneauId, index: c.index, sommet: c.sommet };
    case 'supprimer': return { type: 'inserer', anneauId: c.anneauId, index: c.index, sommet: c.sommet };
    case 'remplacerAnneau': return { ...c, avant: c.apres, apres: c.avant };
    case 'ajouterAnneau': return { type: 'retirerAnneau', anneau: c.anneau, index: c.index };
    case 'retirerAnneau': return { type: 'ajouterAnneau', anneau: c.anneau, index: c.index };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// La session d'édition
// ─────────────────────────────────────────────────────────────────────────────

/**
 * L'état vivant de l'édition : la figure, et de quoi défaire chaque geste.
 *
 * ⚠️ `futur` EST VIDÉ DÈS QU'UN NOUVEAU GESTE EST POSÉ. C'est le comportement
 * que tout le monde attend d'un éditeur, et l'oublier produit le pire bogue de
 * cette famille : on annule trois fois, on déplace un sommet, on refait — et
 * l'éditeur rejoue un geste qui portait sur une figure qui n'existe plus.
 */
export interface Session {
  figure: Figure;
  passe: Commande[];
  futur: Commande[];
}

export function ouvrirSession(f: Figure): Session {
  return { figure: copier(f), passe: [], futur: [] };
}

export function executer(s: Session, c: Commande): void {
  appliquer(s.figure, c);
  s.passe.push(c);
  s.futur.length = 0;
}

export function peutAnnuler(s: Session): boolean { return s.passe.length > 0; }
export function peutRefaire(s: Session): boolean { return s.futur.length > 0; }

export function annuler(s: Session): boolean {
  const c = s.passe.pop();
  if (!c) return false;
  appliquer(s.figure, inverser(c));
  s.futur.push(c);
  return true;
}

export function refaire(s: Session): boolean {
  const c = s.futur.pop();
  if (!c) return false;
  appliquer(s.figure, c);
  s.passe.push(c);
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Gestes du géomètre
// ─────────────────────────────────────────────────────────────────────────────

/** Déplace un sommet, en l'accrochant si une cible est à portée. */
export function deplacerSommet(
  s: Session, anneauId: string, sommetId: string, vers: Point, options?: OptionsAccrochage,
): Accroche | null {
  const an = anneauDe(s.figure, anneauId);
  const som = an.sommets.find((x) => x.id === sommetId);
  if (!som) throw new Error(`Sommet ${sommetId} absent.`);
  let cible = vers;
  let acc: Accroche | null = null;
  if (options) {
    acc = accrocher(vers, s.figure, { ...options, ignorer: [...(options.ignorer ?? []), sommetId] });
    if (acc) cible = acc.point;
  }
  executer(s, { type: 'deplacer', anneauId, sommetId, avant: { lon: som.lon, lat: som.lat }, apres: cible });
  return acc;
}

/**
 * Insère un sommet sur le côté le PLUS PROCHE du point donné.
 *
 * ⚠️ CHERCHER LE CÔTÉ, PAS LE SOMMET LE PLUS PROCHE. Insérer « après le sommet
 * le plus proche » place le point du mauvais côté une fois sur deux, dès que
 * l'angle est aigu — et le contour se croise sans que le geste ait rien d'anormal.
 */
export function insererSommetSurCote(s: Session, p: Point, options?: { toleranceM?: number }): Sommet | null {
  let meilleur: { anneauId: string; index: number; point: Point; d: number } | null = null;
  for (const sg of segments(s.figure)) {
    const pr = projeterSurSegment(p, sg.a, sg.b);
    if (!meilleur || pr.distanceM < meilleur.d) {
      meilleur = { anneauId: sg.anneauId, index: sg.index, point: pr.point, d: pr.distanceM };
    }
  }
  if (!meilleur) return null;
  if (options?.toleranceM !== undefined && meilleur.d > options.toleranceM) return null;
  const nouveau = sommet(meilleur.point.lon, meilleur.point.lat);
  executer(s, { type: 'inserer', anneauId: meilleur.anneauId, index: meilleur.index + 1, sommet: nouveau });
  return nouveau;
}

/**
 * Supprime un sommet — SAUF si cela réduirait l'anneau sous trois sommets.
 *
 * ⚠️ LE REFUS EST EXPLICITE, il ne se fait pas en silence. Un éditeur qui
 * ignore la demande laisse l'agent recliquer trois fois en croyant que le clic
 * n'a pas porté.
 */
export function supprimerSommet(s: Session, anneauId: string, sommetId: string): { fait: boolean; motif?: string } {
  const an = anneauDe(s.figure, anneauId);
  if (an.sommets.length <= 3) {
    return { fait: false, motif: 'Un anneau ne peut pas descendre sous trois sommets.' };
  }
  const index = an.sommets.findIndex((x) => x.id === sommetId);
  const vise = index < 0 ? undefined : an.sommets[index];
  if (!vise) return { fait: false, motif: 'Ce sommet n\'appartient pas à cet anneau.' };
  executer(s, { type: 'supprimer', anneauId, index, sommet: { ...vise } });
  return { fait: true };
}

/**
 * Orthogonalise un anneau : redresse les angles proches de 90° sans toucher
 * à ceux qui ne le sont pas.
 *
 * ⚠️ LE SEUIL EXISTE POUR QUE LE GESTE SOIT SÛR. Sans lui, « orthogonaliser »
 * transformerait un terrain en L ou en triangle en quelque chose que le
 * géomètre n'a pas levé. On ne redresse que ce qui était manifestement destiné
 * à être droit, et on laisse le reste intact.
 */
export function orthogonaliser(s: Session, anneauId: string, toleranceDeg = 15): number {
  const an = anneauDe(s.figure, anneauId);
  const n = an.sommets.length;
  if (n < 4) return 0;
  const ref = an.sommets[0];
  if (!ref) return 0;
  const plan = planLocal(ref.lat);
  const pts = an.sommets.map((p) => versMetres(p, ref, plan));

  // Direction dominante : la moyenne circulaire des côtés, modulo 90°.
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < n; i += 1) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    if (!a || !b) continue;
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    sx += Math.cos(4 * ang);
    sy += Math.sin(4 * ang);
  }
  if (sx === 0 && sy === 0) return 0;
  const dominante = Math.atan2(sy, sx) / 4;

  let redresses = 0;
  const nouveaux = pts.map((p) => ({ ...p }));
  for (let i = 0; i < n; i += 1) {
    const a = nouveaux[i];
    const b = nouveaux[(i + 1) % n];
    if (!a || !b) continue;
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    // L'axe de grille le plus proche.
    const k = Math.round((ang - dominante) / (Math.PI / 2));
    const cible = dominante + k * (Math.PI / 2);
    const ecart = Math.abs(((ang - cible + Math.PI) % (2 * Math.PI)) - Math.PI);
    if (ecart > (toleranceDeg * Math.PI) / 180) continue;
    // On fait pivoter le côté autour de son milieu : aucune extrémité n'est privilégiée.
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const demi = Math.hypot(b.x - a.x, b.y - a.y) / 2;
    a.x = mx - demi * Math.cos(cible); a.y = my - demi * Math.sin(cible);
    b.x = mx + demi * Math.cos(cible); b.y = my + demi * Math.sin(cible);
    redresses += 1;
  }
  if (redresses === 0) return 0;

  const avant = an.sommets.map((x) => ({ ...x }));
  const apres: Sommet[] = nouveaux.map((m, i) => {
    const d = versDegres(m, ref, plan);
    const origine = an.sommets[i];
    /* Impossible par construction — `nouveaux` est une copie de `pts`, lui-même
       un `map` de `an.sommets`. On le DIT plutôt que de l'assumer : un jour où
       l'on insérera un sommet au milieu de cette fonction, l'erreur nommera sa
       cause au lieu de produire un anneau à l'identifiant vide. */
    if (!origine) throw new Error('orthogonaliser : sommet source introuvable.');
    return { id: origine.id, lon: d.lon, lat: d.lat };
  });
  executer(s, { type: 'remplacerAnneau', anneauId, avant, apres });
  return redresses;
}

// ─────────────────────────────────────────────────────────────────────────────
// Frontières GeoJSON — le SEUL endroit où la fermeture explicite existe
// ─────────────────────────────────────────────────────────────────────────────

export interface PolygoneGeoJSON {
  readonly type: 'Polygon';
  readonly coordinates: number[][][];
}

export interface PointGeoJSON { readonly type: 'Point'; readonly coordinates: number[] }
export interface MultiPointGeoJSON { readonly type: 'MultiPoint'; readonly coordinates: number[][] }
export interface LigneGeoJSON { readonly type: 'LineString'; readonly coordinates: number[][] }

export type GeometrieGeoJSON = PolygoneGeoJSON | PointGeoJSON | MultiPointGeoJSON | LigneGeoJSON;

/**
 * Rend le GeoJSON que `POST /parcelles/verifier-emprise` et `POST /releves`
 * attendent : anneaux fermés, extérieur antihoraire, trous horaires.
 */
/**
 * Le premier corps d'un `MultiPolygon`, et le compte de ceux qu'on laisse.
 *
 * ⚠️⚠️ `cad.parcelle.geom` EST TYPÉE `MULTIPOLYGON`, TOUJOURS. Un éditeur qui
 * attendrait un `Polygon` échouerait sur la TOTALITÉ du registre, et son
 * message parlerait d'une géométrie « illisible » sur une donnée parfaitement
 * bonne — le pire diagnostic possible, celui qui envoie chercher le défaut
 * dans la donnée.
 *
 * ⚠️ ET LE COMPTE EST RENDU, PAS AVALÉ. L'éditeur ne tient qu'un corps : en
 * reprendre un sur trois sans le dire ferait redéposer une parcelle AMPUTÉE de
 * ses autres parties, et l'amputation ne se verrait qu'à la carte, bien plus
 * tard, sans que rien ne la relie au geste qui l'a causée.
 */
export function premierCorps(g: unknown): { polygone: PolygoneGeoJSON; corps: number } | null {
  const brut = g as { type?: string; coordinates?: unknown } | null;
  if (!brut || typeof brut !== 'object') return null;

  if (brut.type === 'Polygon') {
    const co = brut.coordinates as number[][][] | undefined;
    if (!Array.isArray(co) || co.length === 0) return null;
    return { polygone: { type: 'Polygon', coordinates: co }, corps: 1 };
  }

  if (brut.type === 'MultiPolygon') {
    const corps = brut.coordinates as number[][][][] | undefined;
    if (!Array.isArray(corps) || corps.length === 0) return null;
    const premier = corps[0];
    if (!Array.isArray(premier) || premier.length === 0) return null;
    return { polygone: { type: 'Polygon', coordinates: premier }, corps: corps.length };
  }

  return null;
}

export function versGeoJSONNature(f: Figure): GeometrieGeoJSON {
  const nature = natureDe(f);
  const an = f.anneaux[0];

  if (nature === 'point') {
    const pts = (an?.sommets ?? []).map((v) => [v.lon, v.lat]);
    /**
     * ⚠️ UN SEUL POINT REND `Point`, PLUSIEURS RENDENT `MultiPoint` — ce n'est
     * pas une coquetterie. `cad.borne.geom` et les schémas de levé attendent
     * un `MultiPoint` pour un semis ; servir un `Point` unique emballé dans un
     * `MultiPoint` à un champ qui veut un `Point` (ou l'inverse) est refusé
     * par PostGIS, par un message sur le TYPE qui ne dit pas quel champ.
     */
    if (pts.length === 1) return { type: 'Point', coordinates: pts[0]! };
    return { type: 'MultiPoint', coordinates: pts };
  }

  if (nature === 'ligne') {
    // Aucune fermeture : c'est toute la différence avec le polygone.
    return { type: 'LineString', coordinates: (an?.sommets ?? []).map((v) => [v.lon, v.lat]) };
  }

  return versGeoJSON(f);
}

/**
 * Lit n'importe laquelle des trois natures et rend la figure correspondante.
 *
 * ⚠️ LA NATURE VIENT DU `type` DU GeoJSON, JAMAIS D'UN ARGUMENT. La passer à
 * part permettrait de lire une `LineString` « en tant que surface » — ce que le
 * code ferait sans broncher, en refermant la ligne, donc en inventant un
 * segment et une aire.
 */
export function depuisGeoJSONNature(g: GeometrieGeoJSON): Figure {
  if (g.type === 'Point') return semis([{ lon: g.coordinates[0]!, lat: g.coordinates[1]! }]);
  if (g.type === 'MultiPoint') {
    const pts: Point[] = [];
    for (const c of g.coordinates) {
      const [lon, lat] = c;
      if (typeof lon === 'number' && typeof lat === 'number') pts.push({ lon, lat });
    }
    return semis(pts);
  }
  if (g.type === 'LineString') {
    const pts: Point[] = [];
    for (const c of g.coordinates) {
      const [lon, lat] = c;
      if (typeof lon === 'number' && typeof lat === 'number') pts.push({ lon, lat });
    }
    /* ⚠️ ON NE RETIRE AUCUNE FERMETURE ICI. Une ligne dont le dernier point
       rejoint le premier est une BOUCLE réelle — un rond-point, une desserte
       en raquette. La dépouiller la rouvrirait, et raccourcirait la voie. */
    return polyligne(pts);
  }
  return depuisGeoJSON(g);
}

export function versGeoJSON(f: Figure): PolygoneGeoJSON {
  const coordinates = f.anneaux.map((an, rang) => {
    let sommets = an.sommets;
    const signee = aireSigneeM2(an);
    const veutAntihoraire = rang === 0;
    if ((veutAntihoraire && signee < 0) || (!veutAntihoraire && signee > 0)) {
      sommets = [...sommets].reverse();
    }
    const co: number[][] = sommets.map((s) => [s.lon, s.lat]);
    const premier = co[0];
    if (premier) co.push([...premier]); // fermeture explicite, ICI seulement
    return co;
  });
  return { type: 'Polygon', coordinates };
}

/**
 * Lit un GeoJSON et retire la fermeture explicite.
 *
 * ⚠️ NE PAS SE FIER À L'ÉGALITÉ STRICTE POUR REPÉRER LE SOMMET DE FERMETURE.
 * Un flux passé par un appareil de terrain ou une conversion peut rendre
 * `-13.6770000001` là où il avait `-13.677`. On compare au seuil de
 * dégénérescence, en mètres — la même règle que partout ailleurs ici.
 */
export function depuisGeoJSON(g: PolygoneGeoJSON): Figure {
  const anneaux = g.coordinates.map((co) => {
    /* ⚠️ UN COUPLE INCOMPLET EST ÉCARTÉ, PAS COMPLÉTÉ PAR ZÉRO. Un `[lon]` sans
       latitude deviendrait le point (lon, 0) — au large du golfe de Guinée, à
       1 000 km de tout, et la figure resterait « valide ». Mieux vaut un anneau
       court, que `diagnostiquer` refusera en nommant la règle. */
    const pts: Point[] = [];
    for (const c of co) {
      const [lon, lat] = c;
      if (typeof lon === 'number' && typeof lat === 'number') pts.push({ lon, lat });
    }
    const premier = pts[0];
    const dernier = pts[pts.length - 1];
    if (pts.length > 1 && premier && dernier && distanceM(premier, dernier) < SEUIL_DEGENERE_M) pts.pop();
    return anneau(pts);
  });
  return { nature: 'surface', anneaux };
}
